import { useEffect, useMemo, useRef, useState } from "react";
import {
  useAudioPeaks,
  peaksToBars,
  waveformBars,
  WAVEFORM_PX_PER_BAR,
  WAVEFORM_MIN_BARS,
  WAVEFORM_MAX_BARS,
} from "../lib/audioPeaks";
import { setAudioPeaks, type SetAudioPeaksTarget } from "../lib/api";
import { Icon } from "./icons";

interface VoicePreviewPlayerProps {
  audioUrl?: string;
  // Pre-computed peaks (base64 uint8). When present the waveform paints
  // without downloading the audio. Missing on legacy rows — the player
  // decodes once and lazy-backfills via `backfillTarget`.
  peaks?: string;
  // Identifies the asset for backfill. Omitted when the asset isn't owned
  // by the current workspace (e.g. provided voices in another user's
  // workspace) so we never POST peaks for assets the user can't write.
  backfillTarget?: SetAudioPeaksTarget;
  playing: boolean;
  // 0..1 — only consulted while paused; while playing, rAF drives clip-path.
  progress: number;
  // Stable seed so the synthetic fallback waveform doesn't reshuffle.
  seed: number;
  onPlayPause: (e: React.MouseEvent) => void;
  onSeek: (fraction: number, e: React.MouseEvent) => void;
  onTimeUpdate: (el: HTMLAudioElement) => void;
  onLoadedMetadata: (el: HTMLAudioElement) => void;
  onEnded: () => void;
  audioRef: (el: HTMLAudioElement | null) => void;
  durationLabel?: string;
}

export function VoicePreviewPlayer({
  audioUrl,
  peaks: prePeaks,
  backfillTarget,
  playing,
  progress,
  seed,
  onPlayPause,
  onSeek,
  onTimeUpdate,
  onLoadedMetadata,
  onEnded,
  audioRef,
  durationLabel,
}: VoicePreviewPlayerProps) {
  const waveRef = useRef<HTMLDivElement | null>(null);
  const localAudioRef = useRef<HTMLAudioElement | null>(null);
  const playedLayerRef = useRef<HTMLDivElement | null>(null);
  const [waveWidth, setWaveWidth] = useState(0);

  useEffect(() => {
    const el = waveRef.current;
    if (!el) return;
    setWaveWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setWaveWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      const audio = localAudioRef.current;
      const layer = playedLayerRef.current;
      if (audio && layer && audio.duration && Number.isFinite(audio.duration)) {
        const f = Math.max(0, Math.min(1, audio.currentTime / audio.duration));
        layer.style.clipPath = `inset(0 ${((1 - f) * 100).toFixed(3)}% 0 0)`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  const barCount = Math.max(
    WAVEFORM_MIN_BARS,
    Math.min(WAVEFORM_MAX_BARS, Math.floor(waveWidth / WAVEFORM_PX_PER_BAR) || WAVEFORM_MIN_BARS)
  );
  // Memoise the backfill descriptor so useAudioPeaks doesn't see a fresh
  // identity on every render (otherwise the decode-then-POST effect re-runs).
  const backfill = useMemo(
    () =>
      !prePeaks && backfillTarget
        ? {
            onBackfill: (encoded: string) => {
              void setAudioPeaks(backfillTarget, encoded).catch(() => {
                // Best-effort — never surface backfill errors.
              });
            },
          }
        : undefined,
    [prePeaks, backfillTarget],
  );
  const peaks = useAudioPeaks(audioUrl, prePeaks, backfill);
  const bars = peaks ? peaksToBars(peaks, barCount) : waveformBars(seed, barCount);

  const playedClipStyle: React.CSSProperties | undefined = playing
    ? undefined
    : { clipPath: `inset(0 ${((1 - progress) * 100).toFixed(3)}% 0 0)` };

  return (
    <div className="vc-preview-row">
      <button
        type="button"
        className={`play-btn ${playing ? "playing" : ""}`}
        onClick={onPlayPause}
        title={playing ? "Pause preview" : "Play preview"}
        aria-label={playing ? "Pause preview" : "Play preview"}
      >
        {playing ? <Icon.Pause /> : <Icon.Play />}
      </button>
      <audio
        ref={(el) => {
          localAudioRef.current = el;
          audioRef(el);
        }}
        src={audioUrl}
        onTimeUpdate={(e) => onTimeUpdate(e.currentTarget)}
        onLoadedMetadata={(e) => onLoadedMetadata(e.currentTarget)}
        onEnded={onEnded}
        // preload="none" so 30 voice-preview cards on first paint don't fire
        // 30 metadata GETs. The audio is only fetched once the user hits play.
        preload="none"
      />
      <div
        ref={waveRef}
        className="take-wave"
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          const fraction = (e.clientX - rect.left) / rect.width;
          onSeek(fraction, e);
        }}
        role="button"
        aria-label="Seek preview"
        title="Click to seek"
      >
        <div className="take-wave-layer base">
          {bars.map((h, i) => (
            <i key={i} style={{ height: `${h}px` }} />
          ))}
        </div>
        <div ref={playedLayerRef} className="take-wave-layer played" style={playedClipStyle}>
          {bars.map((h, i) => (
            <i key={i} style={{ height: `${h}px` }} />
          ))}
        </div>
      </div>
      {durationLabel && <div className="take-time">{durationLabel}</div>}
    </div>
  );
}

interface GeneratePreviewButtonProps {
  status: "idle" | "queued" | "processing" | "failed";
  retrying: boolean;
  disabled: boolean;
  errorTitle?: string;
  onClick: (e: React.MouseEvent) => void;
}

export function GeneratePreviewButton({
  status,
  retrying,
  disabled,
  errorTitle,
  onClick,
}: GeneratePreviewButtonProps) {
  const inFlight = status === "queued" || status === "processing" || retrying;
  const failed = status === "failed";

  let label: string;
  let icon: React.ReactNode;
  let cls = "vc-preview-cta";

  if (inFlight) {
    label = "Generating preview…";
    icon = <span className="spin-mini" />;
    cls += " is-loading";
  } else if (failed) {
    label = "Retry preview";
    icon = <Icon.Refresh />;
    cls += " is-failed";
  } else {
    label = "Generate preview";
    icon = <Icon.Spark />;
  }

  const title = failed && errorTitle ? `Preview failed: ${errorTitle}` : label;

  return (
    <button
      type="button"
      className={cls}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled || inFlight) return;
        onClick(e);
      }}
      disabled={disabled}
      title={title}
      aria-label={title}
    >
      <span className="vc-preview-cta-ico">{icon}</span>
      <span className="vc-preview-cta-lbl">{label}</span>
    </button>
  );
}
